(function () {
  if (window.SolvraCarousel) return;

  class SolvraCarousel {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-carousel-viewport]');
      this.track = root.querySelector('[data-carousel-track]');
      this.slides = this.track ? Array.from(this.track.children) : [];
      this.prevButton = root.querySelector('[data-carousel-prev]');
      this.nextButton = root.querySelector('[data-carousel-next]');
      this.index = 0;
      this.touchStartX = 0;
      this.hoverPaused = false;
      this.autoplayPausedUntil = 0;
      this.autoplayTimer = null;
      this.autoplayEnabled = root.dataset.autoplay === 'true';
      this.autoplayInterval = (parseInt(root.dataset.autoplayInterval, 10) || 4) * 1000;
      this.onResize = () => this.refresh();
      this.onMouseEnter = () => {
        this.hoverPaused = true;
      };
      this.onMouseLeave = () => {
        this.hoverPaused = false;
      };

      if (!this.viewport || !this.track || this.slides.length === 0) return;

      this.prevButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));
      this.viewport.addEventListener('touchstart', (event) => {
        this.touchStartX = event.touches[0].clientX;
      }, { passive: true });
      this.viewport.addEventListener('touchend', (event) => {
        const delta = this.touchStartX - event.changedTouches[0].clientX;
        if (Math.abs(delta) < 40) return;
        this.move(delta > 0 ? 1 : -1);
        this.pauseAutoplayTemporarily(4000);
      }, { passive: true });

      this.root.addEventListener('mouseenter', this.onMouseEnter);
      this.root.addEventListener('mouseleave', this.onMouseLeave);
      window.addEventListener('resize', this.onResize);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.refresh());
        this.resizeObserver.observe(this.viewport);
      }

      this.refresh();
      this.startAutoplay();
    }

    destroy() {
      this.stopAutoplay();
      window.removeEventListener('resize', this.onResize);
      this.root.removeEventListener('mouseenter', this.onMouseEnter);
      this.root.removeEventListener('mouseleave', this.onMouseLeave);
      this.resizeObserver?.disconnect();
      delete this.root._solvraCarousel;
      this.root.dataset.carouselReady = 'false';
    }

    isActive() {
      const section = this.root.closest('[data-mobile-layout]');
      const mobileLayout = section?.dataset.mobileLayout;
      if (mobileLayout === 'grid' && window.matchMedia('(max-width: 749px)').matches) {
        return false;
      }
      return true;
    }

    getVisibleCount() {
      const styles = getComputedStyle(this.root);
      const desktop = parseInt(styles.getPropertyValue('--solvra-carousel-desktop-visible'), 10) || 4;
      const mobile = parseInt(styles.getPropertyValue('--solvra-carousel-mobile-visible'), 10) || 1;
      return window.matchMedia('(min-width: 750px)').matches ? desktop : mobile;
    }

    getGap() {
      return parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || '0');
    }

    getMaxIndex() {
      return Math.max(0, this.slides.length - this.getVisibleCount());
    }

    pauseAutoplayTemporarily(duration) {
      this.autoplayPausedUntil = Date.now() + duration;
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplayEnabled || !this.isActive()) return;

      this.autoplayTimer = window.setInterval(() => {
        if (!this.isActive()) return;
        if (this.hoverPaused) return;
        if (Date.now() < this.autoplayPausedUntil) return;
        this.advanceAutoplay();
      }, this.autoplayInterval);
    }

    stopAutoplay() {
      if (this.autoplayTimer) {
        window.clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
    }

    advanceAutoplay() {
      const maxIndex = this.getMaxIndex();
      if (maxIndex === 0) return;
      this.index = this.index >= maxIndex ? 0 : this.index + 1;
      this.update(false);
    }

    move(direction) {
      if (!this.isActive()) return;
      this.pauseAutoplayTemporarily(4000);
      const maxIndex = this.getMaxIndex();
      this.index = Math.min(maxIndex, Math.max(0, this.index + direction));
      this.update(true);
    }

    refresh() {
      if (!this.isActive()) {
        this.track.style.transform = 'none';
        this.slides.forEach((slide) => {
          slide.style.flex = '';
          slide.style.maxWidth = '';
        });
        this.stopAutoplay();
        return;
      }

      const visible = this.getVisibleCount();
      const gap = this.getGap();
      const viewportWidth = this.viewport.clientWidth;
      const slideWidth = (viewportWidth - gap * (visible - 1)) / visible;

      this.slides.forEach((slide) => {
        slide.style.flex = `0 0 ${slideWidth}px`;
        slide.style.maxWidth = `${slideWidth}px`;
      });

      this.index = Math.min(this.getMaxIndex(), this.index);
      this.update(true);
      this.startAutoplay();
    }

    update(updateButtons) {
      if (!this.isActive()) {
        this.track.style.transform = 'none';
        return;
      }

      const gap = this.getGap();
      const slideWidth = this.slides[0]?.getBoundingClientRect().width || 0;
      const offset = this.index * (slideWidth + gap);
      this.track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (!updateButtons) return;

      const maxIndex = this.getMaxIndex();
      if (this.prevButton) {
        this.prevButton.disabled = this.index <= 0;
        this.prevButton.classList.toggle('is-disabled', this.index <= 0);
      }
      if (this.nextButton) {
        this.nextButton.disabled = this.index >= maxIndex;
        this.nextButton.classList.toggle('is-disabled', this.index >= maxIndex);
      }
    }
  }

  window.SolvraCarousel = SolvraCarousel;

  window.initSolvraCarousel = function (root) {
    if (!root) return null;
    if (root._solvraCarousel) {
      root._solvraCarousel.destroy();
    }
    root.dataset.carouselReady = 'true';
    root._solvraCarousel = new SolvraCarousel(root);
    return root._solvraCarousel;
  };

  function initAllSolvraCarousels(scope) {
    const rootEl = scope && scope.querySelectorAll ? scope : document;
    rootEl.querySelectorAll('[data-solvra-carousel]').forEach((root) => {
      window.initSolvraCarousel(root);
    });
  }

  window.initAllSolvraCarousels = initAllSolvraCarousels;

  document.addEventListener('shopify:section:load', (event) => {
    initAllSolvraCarousels(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-solvra-carousel]').forEach((root) => {
      root._solvraCarousel?.destroy();
    });
  });

  // Auto-init when this deferred script loads (covers race with inline section boots).
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAllSolvraCarousels(document));
  } else {
    initAllSolvraCarousels(document);
  }
})();
